import { ArchitectureParseError } from "../errors";
import type { Assumption } from "../types";
import { asBoolean, asString } from "./parse-bullets-v3";

// Coerce js-yaml output into structured Assumption[].
//
// Required: `description` (non-empty), `verified` (boolean). When
// `verified === false`, `how_to_verify` must be present and non-empty —
// the whole point of the assumption is to flag the verification path.

interface AssumptionParseContext {
  scope: string;
  strict: boolean;
}

export function parseAssumptions(value: unknown, ctx: AssumptionParseContext): Assumption[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return [];
  const out: Assumption[] = [];
  for (let i = 0; i < value.length; i += 1) {
    const entry = value[i];
    const a = coerce(entry, {
      ...ctx,
      scope: `${ctx.scope}.assumptions[${i}]`,
    });
    if (a) out.push(a);
  }
  return out;
}

function coerce(value: unknown, ctx: AssumptionParseContext): Assumption | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope}: expected an assumption object (description, verified, how_to_verify?).`,
      );
    }
    return null;
  }
  const rec = value as Record<string, unknown>;
  const description = asString(rec.description) ?? "";
  if (description === "") {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope}: \`description\` is required and must be non-empty.`,
      );
    }
    return null;
  }
  const verified = asBoolean(rec.verified);
  if (verified === undefined) {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope}: \`verified\` is required and must be boolean (true/false).`,
      );
    }
    return null;
  }
  const how_to_verify = asString(rec.how_to_verify);
  if (verified === false && (!how_to_verify || how_to_verify.trim() === "")) {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope}: unverified assumptions must include \`how_to_verify\`. If you can't articulate how to verify it, you don't yet know it's an assumption.`,
      );
    }
    return null;
  }
  return how_to_verify ? { description, verified, how_to_verify } : { description, verified };
}
